import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "./common/Layout";
import { get_account_list } from "../apis/GetAccountList";
import "../css/myAccount.scss";
import Cookies from "js-cookie";
import { setAPIAuthorizationToken } from "../apis/API";
import Loading from "./common/Loading";
import Pagination from "./common/Pagination";

const MyAccount = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [accountData, setAccountData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  // 搜索关键词状态
  const [searchKeyword, setSearchKeyword] = useState("");
  // 实际用于查询的关键词
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  // 初始化token
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
  }, []);

  // 平台类型显示映射
  const getPlatformTypeLabel = (platformType) => {
    const typeMap = {
      ecommerce: "电商平台",
      service: "服务",
      other: "其他",
    };
    return typeMap[platformType] || platformType;
  };

  // 获取账户数据
  const fetchAccountData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await get_account_list(
        activeSearchKeyword,
        currentPage,
        pageSize
      );

      if (response.data && response.data.code === 200) {
        const listData = response.data.data.list || [];
        const total = response.data.data.pagination?.total || 0;
        setAccountData(listData);
        setTotalItems(total);
      } else {
        console.error("获取账户列表失败:", response.data?.message);
        setAccountData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("获取账户列表出错:", error);
      setAccountData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, activeSearchKeyword]);

  // 当标签页、页码或页大小变化时重新获取数据
  useEffect(() => {
    fetchAccountData();
  }, [fetchAccountData]);

  // 处理查询按钮点击
  const handleSearch = () => {
    setActiveSearchKeyword(searchKeyword); // 更新实际查询关键词
    setCurrentPage(1); // 重置到第一页
  };

  // 处理重置按钮点击
  const handleReset = () => {
    setSearchKeyword("");
    setActiveSearchKeyword(""); // 清空实际查询关键词
    setCurrentPage(1); // 重置到第一页
  };

  // 处理补传资料按钮点击
  const handleUploadDocuments = () => {
    setShowModal(true);
  };

  // 关闭模态框
  const handleCloseModal = () => {
    setShowModal(false);
  };

  // 查询详情
  const handleViewDetail = (accountId) => {
    navigate(`/store-detail/${accountId}`);
  };

  // 计算总页数
  const totalPages = Math.ceil(totalItems / pageSize);

  // 处理页码变化
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // 处理每页显示数量变化
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1); // 重置到第一页
  };

  return (
    <Layout>
      <div className="my-account">
        {/* 标签页 */}
        <div className="tabs-container">
          <div className="tab-item active">收款账户</div>
        </div>

        {/* 搜索区域 */}
        <div className="filter-section">
          <div className="filter-row">
            <input
              type="text"
              className="filter-input"
              placeholder="搜索店铺名称、账号、备注等"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
            <button className="btn-primary" onClick={handleSearch}>
              查询
            </button>
            <button className="btn-secondary" onClick={handleReset}>
              重置
            </button>
          </div>
        </div>

        {/* 操作按钮区域 */}
        <div className="action-buttons account-buttons-apply">
          <button className="btn-add" onClick={handleUploadDocuments}>
            <span className="icon-plus">+</span> 申请电商收款账户
          </button>
          <button className="btn-outline" onClick={handleUploadDocuments}>
            <span className="icon-export">↓</span> 导出
          </button>
          <button className="btn-outline" onClick={handleUploadDocuments}>
            批量注销
          </button>
        </div>

        {/* 表格 */}
        <div className="table-container">
          {loading ? (
            <Loading />
          ) : accountData.length === 0 ? (
            <div
              className="empty-container"
              style={{ textAlign: "center", padding: "40px" }}
            >
              <p>暂无数据</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>用户ID</th>

                  <th>平台名称</th>
                  <th>账号</th>
                  <th>币种</th>
                  <th>余额</th>
                  <th>平台类型</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {accountData.map((account) => (
                  <tr key={account.id}>
                    <td>{account.id}</td>
                    <td>{account.user_id}</td>
                    <td>{account.platform_name}</td>
                    <td>{account.account_number}</td>
                    <td>{account.currency}</td>
                    <td>{account.available_balance}</td>{" "}
                    <td>{getPlatformTypeLabel(account.platform_type)}</td>
                    <td>{account.account_status}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-detail"
                          onClick={() => handleViewDetail(account.id)}
                        >
                          详情
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 分页 */}
        {!loading && accountData.length > 0 && (
          <Pagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>

      <footer className="page-footer">
        <p>© 2022 GLOBAL E-PAYMENT LIMITED. All Rights Reserved.</p>
      </footer>

      {/* 未授权访问提示 */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon">🔒</div>
            <h2 className="modal-title">未授权访问</h2>
            <p className="modal-message">您当前没有权限访问此功能</p>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default MyAccount;
